/**
 * Status-Cookie für Seiten-Caches mit "Vary Cookies" (z. B. Powered Cache).
 *
 * Das Consent-Cookie trägt JSON mit Zeitstempel und ist damit bei jedem
 * Besucher anders. pixelmate_consent_state fasst die Entscheidung auf die
 * Fälle zusammen, die der Server unterschiedlich rendert:
 * "accept.<version>", "reject.<version>" und "custom" (bleibt ungecacht).
 *
 * Gegenstück und Prüfung: include/ConsentCacheState.php. Das Cookie läuft
 * nie länger als das Consent-Cookie, sonst bekäme ein Besucher ohne
 * Einwilligung die Seite für "Alle akzeptieren" aus dem Cache.
 */
export class ConsentCacheState {
    static COOKIE = 'pixelmate_consent_state';

    static OPTIONAL_CATEGORIES = ['analytics', 'marketing', 'external'];

    /**
     * Status einer Einwilligung, wie Pixelmate_ConsentCacheState::state_for().
     *
     * @param {Object} consent Consent-Daten (type, categories, services)
     * @returns {'accept'|'reject'|'custom'}
     */
    static stateFor(consent) {
        const categories = consent?.categories || {};
        const services = Object.values(consent?.services || {});

        if (consent?.type === 'accept'
            && ConsentCacheState.OPTIONAL_CATEGORIES.every(category => categories[category] === true)
            && !services.includes(false)) {
            return 'accept';
        }
        if (ConsentCacheState.OPTIONAL_CATEGORIES.some(category => categories[category] === true)
            || services.includes(true)) {
            return 'custom';
        }
        return 'reject';
    }

    /**
     * Cookie-Wert, wie Pixelmate_ConsentCacheState::value_for().
     *
     * @param {Object} consent
     * @returns {string}
     */
    static valueFor(consent) {
        const state = ConsentCacheState.stateFor(consent);
        if (state === 'custom') {
            return state;
        }
        const version = String(consent?.version || '').replace(/[^A-Za-z0-9]/g, '');
        return version ? `${state}.${version}` : state;
    }

    /**
     * Schreibt das Status-Cookie zu einer gerade gespeicherten Einwilligung.
     *
     * @param {Object} consent
     * @param {number|null} days Laufzeit des Consent-Cookies, null = Sitzung
     * @param {Object} [options] path, sameSite, secure wie beim Consent-Cookie
     */
    static write(consent, days, options = {}) {
        const parts = [
            `${ConsentCacheState.COOKIE}=${ConsentCacheState.valueFor(consent)}`,
            `path=${options.path || '/'}`,
            `SameSite=${options.sameSite || 'Lax'}`
        ];
        if (days !== null && days !== undefined) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            parts.push(`expires=${expires.toUTCString()}`);
        }
        if (options.secure) {
            parts.push('Secure');
        }
        document.cookie = parts.join('; ');
    }

    /**
     * Gleicht das Status-Cookie beim Seitenaufruf mit dem Consent-Cookie ab:
     * Einwilligung von vor diesem Update, Cookie anderer Schreiber (z. B.
     * Shortcode ohne Banner) oder abgelaufenes Consent-Cookie. Neu
     * geschrieben wird nur als Sitzungs-Cookie, weil die Laufzeit des
     * Consent-Cookies hier nicht bekannt ist.
     *
     * @param {Object|null} consent Aktuelles Consent-Cookie oder null
     * @param {Object} [options] path, sameSite, secure
     */
    static sync(consent, options = {}) {
        const current = ConsentCacheState.read();

        if (!consent || typeof consent !== 'object') {
            if (current !== null) {
                document.cookie = `${ConsentCacheState.COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${options.path || '/'}`;
            }
            return;
        }
        if (current !== ConsentCacheState.valueFor(consent)) {
            ConsentCacheState.write(consent, null, options);
        }
    }

    /**
     * @returns {string|null} Aktueller Wert des Status-Cookies
     */
    static read() {
        const prefix = `${ConsentCacheState.COOKIE}=`;
        const entry = document.cookie.split(';').map(part => part.trim()).find(part => part.indexOf(prefix) === 0);
        return entry === undefined ? null : entry.substring(prefix.length);
    }
}
